/**
 * Which billing package may be removed — the client half of the server's
 * empty-package delete guard (`apps/api/src/submissions/delete.ts`).
 *
 * A PCO is created with no submission at all: the FIRST SAVED BLOCK opens one
 * as the collecting default. The shell appears when that happens before the
 * numbering is settled — the PM then starts the real number (Start Next takes
 * over as the default) and the week follows it, leaving the auto-opened
 * package empty and demoted. It reads "No weeks yet · $0.00" from then on, it
 * squats on a number a historical row may need, and as a SECOND open
 * submission it makes the inbox save flow offer a submission picker, which is
 * a misfile target.
 *
 * The four conditions are the server's — the same set, for the same reasons
 * (the server tests them in a different order, which only changes which
 * refusal a doubly-disqualified package earns) — so the action is offered on
 * exactly the packages the API will accept, never on one whose only possible
 * outcome is a failure toast:
 *
 *   open              a closed package went to the GC; its number is printed
 *                     on an xlsx and a cover email out there
 *   no weeks          weeks are billing data; send, transfer or reopen them
 *   not the default   it is the PCO's collecting home
 *   no imported total pre-portal history is a ledger row, removed through its
 *                     own editor ("Delete row"), with its own guards
 *
 * A false answer never hides a capability the PM has: everything it excludes
 * is something the server refuses.
 */
import type { SubmissionStatus } from '@coffman-tm/shared';

/** What this decision needs off a SubmissionSummary. */
export interface RemovablePackage {
  status: SubmissionStatus;
  rollups: readonly unknown[];
  is_default_target: boolean;
  imported_total_cents: number | null;
}

export function canRemoveEmptyPackage(sub: RemovablePackage): boolean {
  return (
    sub.status === 'open' &&
    sub.rollups.length === 0 &&
    !sub.is_default_target &&
    sub.imported_total_cents == null
  );
}
